import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { accountAPI, contactAPI } from '../services/api';
import './Auth.css';

const PLAN_LABELS = {
  free_trial: 'Free Trial',
  silver: 'Silver',
  gold: 'Gold',
  gold_yearly: 'Gold Yearly',
};

const PLAN_DESCRIPTIONS = {
  free_trial: '1 connection, 1 worksheet, run reports only.',
  silver: '2 connections, 2 worksheets, reports + HCM REST.',
  gold: 'Unlimited connections, unlimited worksheets, and all query types.',
  gold_yearly: 'Unlimited connections, worksheets, and AI Sidekick.',
};

const purchaseUrl = process.env.REACT_APP_PURCHASE_URL || 'https://www.slpmicrosystems.com/cloudfish/purchase';

const LogoFish = () => (
  <img src="/cloudfish-fish.png" alt="" aria-hidden="true" className="cloudfish-fish-img" />
);

const CloudFishBg = () => (
  <div className="cloudfish-bg" aria-hidden="true">
    <div className="cloudfish-cloud cloudfish-cloud--1" />
    <div className="cloudfish-cloud cloudfish-cloud--2" />
    <div className="cloudfish-cloud cloudfish-cloud--3" />
    <div className="cloudfish-cloud cloudfish-cloud--4" />
    <div className="cloudfish-cloud cloudfish-cloud--5" />
    <div className="cloudfish-fish cloudfish-fish--1"><LogoFish /></div>
    <div className="cloudfish-fish cloudfish-fish--2"><LogoFish /></div>
    <div className="cloudfish-fish cloudfish-fish--3"><LogoFish /></div>
    <div className="cloudfish-fish cloudfish-fish--4"><LogoFish /></div>
    <div className="cloudfish-fish cloudfish-fish--5"><LogoFish /></div>
  </div>
);

const Account = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [emailForm, setEmailForm] = useState({ newEmail: '', password: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [contactSending, setContactSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await accountAPI.getMe();
        if (!cancelled) setAccount(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load account');
          toast.error('Failed to load account');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await accountAPI.changePassword(passwordForm.current, passwordForm.new);
      toast.success('Password updated successfully');
      setPasswordForm({ current: '', new: '', confirm: '' });
      setShowPasswordForm(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.newEmail.trim()) {
      toast.error('Enter new email');
      return;
    }
    setEmailLoading(true);
    try {
      const res = await accountAPI.changeEmail(emailForm.newEmail, emailForm.password);
      toast.success('Email updated. Please log in again with your new email.');
      setAccount((a) => (a ? { ...a, email: res.data.email } : a));
      setEmailForm({ newEmail: '', password: '' });
      setShowEmailForm(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.email?.trim() || !contactForm.message?.trim()) {
      toast.error('Email and message are required');
      return;
    }
    setContactSending(true);
    try {
      await contactAPI.submit(contactForm);
      toast.success('Message sent. We\'ll respond as soon as possible.');
      setContactForm({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      setShowContactModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send message. Please try again or email us directly.');
    } finally {
      setContactSending(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-page-wrapper auth-page-wrapper--account">
        <CloudFishBg />
        <div className="auth-container">
          <div className="auth-card">
            <p className="auth-hint">Loading account...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="auth-page-wrapper auth-page-wrapper--account">
        <CloudFishBg />
        <div className="auth-container">
          <div className="auth-card">
            <h1>Account</h1>
            <p className="auth-error">{error || 'Could not load account.'}</p>
            <button type="button" className="auth-btn" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-wrapper auth-page-wrapper--account">
      <CloudFishBg />
      <div className="auth-container">
        <div className="account-card auth-card">
          <div className="auth-header">
            <h1>Account Settings</h1>
            <p>Manage your CloudFish plan</p>
          </div>

          <div className="account-section">
            <h3>Username (email)</h3>
            <p className="account-value">{account.email}</p>
            {!showEmailForm ? (
              <button type="button" className="auth-btn-link" onClick={() => setShowEmailForm(true)}>
                Change email
              </button>
            ) : (
              <form onSubmit={handleChangeEmail} className="auth-form" style={{ marginTop: '0.5rem' }}>
                <div className="form-group">
                  <label>New email</label>
                  <input
                    type="email"
                    value={emailForm.newEmail}
                    onChange={(e) => setEmailForm((p) => ({ ...p, newEmail: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={emailForm.password}
                    onChange={(e) => setEmailForm((p) => ({ ...p, password: e.target.value }))}
                    placeholder="••••••••"
                    required
                  />
                </div>
                <button type="submit" className="auth-btn auth-btn-outline" disabled={emailLoading}>
                  {emailLoading ? 'Updating...' : 'Update email'}
                </button>
                <button type="button" className="auth-btn auth-btn-ghost" onClick={() => { setShowEmailForm(false); setEmailForm({ newEmail: '', password: '' }); }}>
                  Cancel
                </button>
              </form>
            )}
          </div>

          <div className="account-section">
            <h3>Password</h3>
            <p className="account-value">••••••••</p>
            {!showPasswordForm ? (
              <button type="button" className="auth-btn-link" onClick={() => setShowPasswordForm(true)}>
                Update password
              </button>
            ) : (
              <form onSubmit={handleChangePassword} className="auth-form" style={{ marginTop: '0.5rem' }}>
                <div className="form-group">
                  <label htmlFor="current-password">Current Password</label>
                  <input
                    type="password"
                    id="current-password"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm((p) => ({ ...p, current: e.target.value }))}
                    placeholder="••••••••"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-password">New Password</label>
                  <input
                    type="password"
                    id="new-password"
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm((p) => ({ ...p, new: e.target.value }))}
                    placeholder="••••••••"
                    minLength={6}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirm-password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm((p) => ({ ...p, confirm: e.target.value }))}
                    placeholder="••••••••"
                    minLength={6}
                    required
                  />
                </div>
                <button type="submit" className="auth-btn auth-btn-outline" disabled={passwordLoading}>
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </button>
                <button type="button" className="auth-btn auth-btn-ghost" onClick={() => { setShowPasswordForm(false); setPasswordForm({ current: '', new: '', confirm: '' }); }}>
                  Cancel
                </button>
              </form>
            )}
          </div>

          <div className="account-section">
            <h3>Current plan</h3>
            <p>
              <span className={`account-plan-badge account-plan-badge--${account.plan}`}>
                {PLAN_LABELS[account.plan] || account.plan}
              </span>
              {' '}
              <a href={purchaseUrl} target="_blank" rel="noopener noreferrer" className="auth-btn-link">
                Change plan
              </a>
            </p>
            <p className="account-plan-desc">{PLAN_DESCRIPTIONS[account.plan] || ''}</p>
            {account.plan === 'free_trial' && (account.trial_end != null || account.trial_days_remaining != null) && (
              <p className="account-trial-expiry">
                {account.trial_end && new Date(account.trial_end) < new Date() ? (
                  <>Your free trial expired on {new Date(account.trial_end).toLocaleDateString()}.</>
                ) : account.trial_days_remaining != null && account.trial_days_remaining > 0 ? (
                  <>Your free trial expires in {account.trial_days_remaining === 1 ? '1 day' : `${account.trial_days_remaining} days`}.</>
                ) : account.trial_days_remaining === 0 ? (
                  <>Your free trial expires today.</>
                ) : null}
              </p>
            )}
          </div>

          <div className="account-section">
            <h3>Need help?</h3>
            <div className="account-actions">
              <button
                type="button"
                className="auth-btn auth-btn-outline"
                onClick={() => {
                  setContactForm((p) => ({ ...p, email: account?.email || p.email }));
                  setShowContactModal(true);
                }}
              >
                Contact us
              </button>
            </div>
          </div>

          <div className="account-footer">
            <button type="button" className="auth-btn" onClick={() => navigate('/dashboard')}>
              Back to CloudFish
            </button>
            <button type="button" className="auth-btn auth-btn-ghost" onClick={handleLogout}>
              Log out
            </button>
          </div>
        </div>
      </div>

      {showContactModal && (
        <div className="contact-modal-overlay" onClick={() => !contactSending && setShowContactModal(false)} role="dialog" aria-modal="true" aria-labelledby="contact-modal-title">
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="contact-modal-close" onClick={() => !contactSending && setShowContactModal(false)} aria-label="Close">×</button>
            <h2 id="contact-modal-title" className="contact-modal-title">Contact Us</h2>
            <p className="contact-modal-desc">Get in touch with us. We&apos;ll respond as soon as possible.</p>
            <form onSubmit={handleContactSubmit} className="contact-modal-form">
              <div className="contact-modal-row">
                <div className="form-group">
                  <label htmlFor="contact-first">First Name <span className="required">*</span></label>
                  <input id="contact-first" type="text" value={contactForm.firstName} onChange={(e) => setContactForm((p) => ({ ...p, firstName: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-last">Last Name <span className="required">*</span></label>
                  <input id="contact-last" type="text" value={contactForm.lastName} onChange={(e) => setContactForm((p) => ({ ...p, lastName: e.target.value }))} required />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="contact-email">Email <span className="required">*</span></label>
                <input id="contact-email" type="email" value={contactForm.email} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label htmlFor="contact-phone">Phone Number</label>
                <input id="contact-phone" type="tel" value={contactForm.phone} onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label htmlFor="contact-message">Message <span className="required">*</span></label>
                <textarea id="contact-message" value={contactForm.message} onChange={(e) => setContactForm((p) => ({ ...p, message: e.target.value }))} placeholder="Tell us how we can help..." rows={4} required />
              </div>
              <div className="contact-modal-actions">
                <button type="submit" className="auth-btn contact-btn-submit" disabled={contactSending}>{contactSending ? 'Sending...' : 'Send Message'}</button>
                <button type="button" className="auth-btn auth-btn-outline" onClick={() => !contactSending && setShowContactModal(false)} disabled={contactSending}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
